/**
 * Nosto-CartRecommendations.js
 *
 * Responsabilidades:
 * - escuchar placements Nosto del cart drawer;
 * - obtener productos desde recommendedProducts o desde el HTML del placement;
 * - extraer product handle, product id y attribution;
 * - renderizar cada producto usando product.nosto-card-compact;
 * - mostrar un producto por slide;
 * - controlar paginación del carrusel;
 * - manejar drag horizontal con Pointer Events para evitar conflictos
 *   con el swipe-to-close vertical del drawer en mobile.
 *
 * No usa el HTML visual generado por el template Nosto.
 * No usa initializeNostoCarrusel().
 */
(function initializeNostoCartRecommendations(global) {
    "use strict";

    class NostoCartRecommendations extends HTMLElement {
        constructor() {
            super();

            this.cards = [];
            this.dragState = new WeakMap();

            this.handleRecommendationsLoaded =
                this.handleRecommendationsLoaded.bind(this);

            this.handleRecommendationsError =
                this.handleRecommendationsError.bind(this);

            this.handleScroll =
                this.handleScroll.bind(this);

            this.handlePointerDown =
                this.handlePointerDown.bind(this);

            this.handlePointerMove =
                this.handlePointerMove.bind(this);

            this.handlePointerUp =
                this.handlePointerUp.bind(this);
        }

        connectedCallback() {
            this.initializeViewService();

            this.addEventListener(
                "nosto:recommendations:loaded",
                this.handleRecommendationsLoaded
            );

            this.addEventListener(
                "nosto:recommendations:error",
                this.handleRecommendationsError
            );

            this.querySelectorAll(
                "[data-cart-nosto-viewport]"
            ).forEach((viewport) => {
                this.bindViewportEvents(
                    viewport
                );
            });
        }

        disconnectedCallback() {
            this.removeEventListener(
                "nosto:recommendations:loaded",
                this.handleRecommendationsLoaded
            );

            this.removeEventListener(
                "nosto:recommendations:error",
                this.handleRecommendationsError
            );

            this.querySelectorAll(
                "[data-cart-nosto-viewport]"
            ).forEach((viewport) => {
                this.unbindViewportEvents(
                    viewport
                );
            });

            this.destroyCards();
        }

        bindViewportEvents(viewport) {
            if (
                viewport.dataset
                    .cartNostoPointerInitialized ===
                "true"
            ) {
                return;
            }

            viewport.dataset
                .cartNostoPointerInitialized =
                "true";

            viewport.addEventListener(
                "scroll",
                this.handleScroll,
                {
                    passive: true,
                }
            );

            viewport.addEventListener(
                "pointerdown",
                this.handlePointerDown
            );

            viewport.addEventListener(
                "pointermove",
                this.handlePointerMove
            );

            viewport.addEventListener(
                "pointerup",
                this.handlePointerUp
            );

            viewport.addEventListener(
                "pointercancel",
                this.handlePointerUp
            );
        }

        unbindViewportEvents(viewport) {
            viewport.removeEventListener(
                "scroll",
                this.handleScroll
            );

            viewport.removeEventListener(
                "pointerdown",
                this.handlePointerDown
            );

            viewport.removeEventListener(
                "pointermove",
                this.handlePointerMove
            );

            viewport.removeEventListener(
                "pointerup",
                this.handlePointerUp
            );

            viewport.removeEventListener(
                "pointercancel",
                this.handlePointerUp
            );

            delete viewport.dataset
                .cartNostoPointerInitialized;

            this.dragState.delete(
                viewport
            );
        }

        initializeViewService() {
            if (
                typeof global.NostoProductViewService !==
                "function"
            ) {
                console.error(
                    "[NostoCartRecommendations] NostoProductViewService no está disponible."
                );

                return;
            }

            this.viewService =
                new global.NostoProductViewService({
                    cardView:
                        "nosto-card-compact",

                    quickBuyView:
                        "nosto-quick-buy",

                    concurrency:
                        4,

                    cache:
                        true,

                    debug:
                        global.NOSTO_DEBUG === true,
                });
        }

        async handleRecommendationsLoaded(event) {
            const recommendationsElement =
                event.target.closest(
                    "nosto-recommendations"
                );

            if (
                !recommendationsElement ||
                !this.contains(
                    recommendationsElement
                )
            ) {
                return;
            }

            const placement =
                recommendationsElement.closest(
                    "[data-cart-nosto-placement]"
                );

            if (!placement) {
                return;
            }

            const detail =
                event.detail || {};

            const products =
                this.resolveProducts(detail);

            if (!products.length) {
                placement.hidden = true;

                console.warn(
                    "[NostoCartRecommendations] El placement no devolvió productos utilizables.",
                    {
                        detail,
                    }
                );

                return;
            }

            await this.renderPlacement(
                placement,
                products,
                {
                    placementId:
                        detail.placementId ||
                        recommendationsElement.dataset
                            .placementId ||
                        "",

                    resultId:
                        detail.resultId ||
                        detail.rawRecommendation
                            ?.result_id ||
                        "",

                    divId:
                        detail.divId ||
                        detail.rawRecommendation
                            ?.div_id ||
                        "",
                }
            );
        }

        /**
         * Primero intenta utilizar los productos normalizados
         * por NostoRecommendations.
         *
         * Si no contienen handle, intenta extraerlos del HTML
         * generado por el template Velocity.
         */
        resolveProducts(detail) {
            const normalizedProducts =
                Array.isArray(
                    detail.recommendedProducts
                )
                    ? detail.recommendedProducts
                    : [];

            const productsFromNormalized =
                normalizedProducts
                    .map((product) =>
                        this.normalizeProduct(
                            product
                        )
                    )
                    .filter(
                        (product) =>
                            product.handle
                    );

            if (
                productsFromNormalized.length
            ) {
                return productsFromNormalized;
            }

            const html =
                String(
                    detail.html ||
                    detail.rawRecommendation?.html ||
                    ""
                ).trim();

            if (!html) {
                return [];
            }

            return this.extractProductsFromHtml(
                html
            );
        }

        /**
         * Extrae los datos mínimos desde:
         *
         * <nosto-product
         *   product-id="..."
         *   product-handle="..."
         *   reco-id="..."
         * >
         */
        extractProductsFromHtml(html) {
            const template =
                document.createElement(
                    "template"
                );

            template.innerHTML =
                html.trim();

            const productElements =
                Array.from(
                    template.content.querySelectorAll(
                        "nosto-product"
                    )
                );

            return productElements
                .map((element) => {
                    const productId =
                        String(
                            element.getAttribute(
                                "product-id"
                            ) || ""
                        ).trim();

                    const handle =
                        String(
                            element.getAttribute(
                                "product-handle"
                            ) || ""
                        ).trim();

                    const attributionKey =
                        String(
                            element.getAttribute(
                                "reco-id"
                            ) || ""
                        ).trim();

                    const productLink =
                        element.querySelector(
                            'a[href*="/products/"]'
                        );

                    const url =
                        productLink?.getAttribute(
                            "href"
                        ) || "";

                    return {
                        id:
                            productId,

                        productId,

                        handle,

                        productHandle:
                            handle,

                        url,

                        attributionKey,
                    };
                })
                .filter(
                    (product) =>
                        product.handle
                );
        }

        normalizeProduct(product) {
            const productId =
                String(
                    product?.productId ||
                    product?.id ||
                    ""
                ).trim();

            const handle =
                String(
                    product?.handle ||
                    product?.productHandle ||
                    ""
                ).trim();

            const attributionKey =
                String(
                    product?.attributionKey ||
                    product?.recoId ||
                    product?.reco_id ||
                    ""
                ).trim();

            return {
                ...product,

                id:
                    productId,

                productId,

                handle,

                productHandle:
                    handle,

                attributionKey,

                url:
                    product?.url ||
                    (
                        handle
                            ? `/products/${encodeURIComponent(
                                handle
                            )}`
                            : ""
                    ),
            };
        }

        async renderPlacement(
            placement,
            products,
            context
        ) {
            const track =
                placement.querySelector(
                    "[data-cart-nosto-track]"
                );

            if (
                !track ||
                !this.viewService ||
                typeof global.NostoProductCard !==
                "function"
            ) {
                return;
            }

            this.destroyCardsInsidePlacement(
                placement
            );

            track.replaceChildren();

            const slides = [];

            const promises =
                products.map(
                    (
                        recommendedProduct,
                        index
                    ) => {
                        const productId =
                            String(
                                recommendedProduct.productId ||
                                recommendedProduct.id ||
                                ""
                            ).trim();

                        const handle =
                            String(
                                recommendedProduct.handle ||
                                recommendedProduct.productHandle ||
                                ""
                            ).trim();

                        if (!handle) {
                            return Promise.resolve(
                                null
                            );
                        }

                        const slide =
                            document.createElement(
                                "div"
                            );

                        slide.className =
                            "cart-nosto-recommendations__slide";

                        slide.dataset.cartNostoSlide =
                            "";

                        slide.dataset.productId =
                            productId;

                        slide.innerHTML = `
                            <div
                                class="cart-nosto-recommendations__skeleton"
                                aria-hidden="true"
                            ></div>
                        `;

                        track.appendChild(
                            slide
                        );

                        slides.push(
                            slide
                        );

                        const hit = {
                            ...recommendedProduct,

                            id:
                                productId,

                            productId,

                            handle,

                            productHandle:
                                handle,

                            url:
                                recommendedProduct.url ||
                                `/products/${encodeURIComponent(
                                    handle
                                )}`,
                        };

                        const attributionKey =
                            String(
                                recommendedProduct
                                    .attributionKey ||
                                context.resultId ||
                                ""
                            ).trim();

                        const card =
                            new global.NostoProductCard({
                                hit,

                                productId,

                                container:
                                    slide,

                                cardType:
                                    "normal",

                                viewName:
                                    "nosto-card-compact",

                                viewService:
                                    this.viewService,

                                source:
                                    "cart-recommendations",

                                track:
                                    "recommendation",

                                attributionKey,

                                context: {
                                    source:
                                        "cart-recommendations",

                                    position:
                                        index,

                                    cardType:
                                        "normal",

                                    placementId:
                                        context.placementId,

                                    resultId:
                                        context.resultId,

                                    divId:
                                        context.divId,

                                    attributionKey,
                                },

                                onError: ({
                                    error,
                                }) => {
                                    console.error(
                                        "[NostoCartRecommendations] Error renderizando producto.",
                                        {
                                            recommendedProduct,
                                            error,
                                        }
                                    );
                                },
                            });

                        this.cards.push({
                            card,
                            placement,
                        });

                        return card.render();
                    }
                );

            const results =
                await Promise.allSettled(
                    promises
                );

            results.forEach(
                (result, index) => {
                    if (
                        result.status !==
                        "fulfilled" ||
                        !result.value
                    ) {
                        slides[
                            index
                        ]?.remove();
                    }
                }
            );

            const renderedSlides =
                Array.from(
                    track.querySelectorAll(
                        "[data-cart-nosto-slide]"
                    )
                );

            if (!renderedSlides.length) {
                placement.hidden =
                    true;

                return;
            }

            placement.hidden =
                false;

            const viewport =
                placement.querySelector(
                    "[data-cart-nosto-viewport]"
                );

            if (viewport) {
                this.bindViewportEvents(
                    viewport
                );

                viewport.scrollLeft =
                    0;
            }

            this.createDots(
                placement,
                renderedSlides.length
            );

            this.updateDots(
                placement,
                0
            );
        }

        handleRecommendationsError(event) {
            const recommendationsElement =
                event.target.closest(
                    "nosto-recommendations"
                );

            if (
                !recommendationsElement ||
                !this.contains(
                    recommendationsElement
                )
            ) {
                return;
            }

            recommendationsElement
                .closest(
                    "[data-cart-nosto-placement]"
                )
                ?.setAttribute(
                    "hidden",
                    ""
                );

            console.error(
                "[NostoCartRecommendations]",
                event.detail?.error
            );
        }

        createDots(
            placement,
            count
        ) {
            const dots =
                placement.querySelector(
                    "[data-cart-nosto-dots]"
                );

            if (!dots) {
                return;
            }

            dots.replaceChildren();

            if (count <= 1) {
                dots.hidden =
                    true;

                return;
            }

            dots.hidden =
                false;

            for (
                let index = 0;
                index < count;
                index += 1
            ) {
                const button =
                    document.createElement(
                        "button"
                    );

                button.type =
                    "button";

                button.className =
                    "cart-nosto-recommendations__dot";

                button.dataset.index =
                    String(index);

                button.setAttribute(
                    "aria-label",
                    `Mostrar producto ${index + 1}`
                );

                button.addEventListener(
                    "click",
                    () => {
                        this.goToSlide(
                            placement,
                            index
                        );
                    }
                );

                dots.appendChild(
                    button
                );
            }
        }

        goToSlide(
            placement,
            index
        ) {
            const viewport =
                placement.querySelector(
                    "[data-cart-nosto-viewport]"
                );

            const slides =
                placement.querySelectorAll(
                    "[data-cart-nosto-slide]"
                );

            const slide =
                slides[index];

            if (
                !viewport ||
                !slide
            ) {
                return;
            }

            viewport.scrollTo({
                left:
                    slide.offsetLeft,

                behavior:
                    "smooth",
            });
        }

        handlePointerDown(event) {
            if (
                event.pointerType === "mouse" &&
                event.button !== 0
            ) {
                return;
            }

            const isDesktop =
                global.matchMedia(
                    "(min-width: 750px)"
                ).matches;

            if (isDesktop) {
                const interactiveElement =
                    event.target.closest(
                        [
                            "button",
                            "a",
                            "input",
                            "select",
                            "textarea",
                            "[role='button']",
                            "[data-nosto-card-quick-add]",
                        ].join(",")
                    );

                if (interactiveElement) {
                    return;
                }
            }

            const viewport =
                event.currentTarget;

            const placement =
                viewport.closest(
                    "[data-cart-nosto-placement]"
                );

            const slides =
                placement
                    ? Array.from(
                        placement.querySelectorAll(
                            "[data-cart-nosto-slide]"
                        )
                    )
                    : [];

            if (slides.length <= 1) {
                return;
            }

            /*
             * Detectamos en qué slide estamos realmente
             * al iniciar el gesto.
             */
            let currentIndex = 0;
            let smallestDistance = Infinity;

            slides.forEach(
                (
                    slide,
                    index
                ) => {
                    const distance =
                        Math.abs(
                            slide.offsetLeft -
                            viewport.scrollLeft
                        );

                    if (
                        distance <
                        smallestDistance
                    ) {
                        smallestDistance =
                            distance;

                        currentIndex =
                            index;
                    }
                }
            );

            this.dragState.set(
                viewport,
                {
                    pointerId:
                        event.pointerId,

                    startX:
                        event.clientX,

                    startY:
                        event.clientY,

                    lastX:
                        event.clientX,

                    lastY:
                        event.clientY,

                    startScrollLeft:
                        viewport.scrollLeft,

                    currentIndex,

                    direction:
                        null,

                    dragging:
                        false,

                    maxDeltaX:
                        0,
                }
            );

            try {
                viewport.setPointerCapture(
                    event.pointerId
                );
            } catch (error) {
                // No-op.
            }
        }

        handlePointerMove(event) {
            const viewport =
                event.currentTarget;

            const state =
                this.dragState.get(
                    viewport
                );

            if (
                !state ||
                state.pointerId !==
                event.pointerId
            ) {
                return;
            }

            const deltaX =
                event.clientX -
                state.startX;

            const deltaY =
                event.clientY -
                state.startY;

            const absX =
                Math.abs(deltaX);

            const absY =
                Math.abs(deltaY);

            /*
             * Guardamos siempre la última posición
             * conocida del dedo.
             */
            state.lastX =
                event.clientX;

            state.lastY =
                event.clientY;

            /*
             * Guardamos también el desplazamiento
             * horizontal máximo alcanzado.
             */
            if (
                absX >
                Math.abs(
                    state.maxDeltaX
                )
            ) {
                state.maxDeltaX =
                    deltaX;
            }

            /*
             * Esperamos unos píxeles antes
             * de decidir la dirección.
             */
            if (!state.direction) {
                if (
                    absX < 5 &&
                    absY < 5
                ) {
                    return;
                }

                state.direction =
                    absX >= absY
                        ? "horizontal"
                        : "vertical";
            }

            if (
                state.direction !==
                "horizontal"
            ) {
                return;
            }

            event.preventDefault();

            if (!state.dragging) {
                state.dragging =
                    true;

                viewport.classList.add(
                    "is-dragging"
                );

                viewport.style
                    .scrollSnapType =
                    "none";
            }

            /*
             * Seguimos intentando mover visualmente
             * el carrusel con el dedo.
             *
             * Pero el cambio definitivo de slide
             * ya NO dependerá de este scrollLeft.
             */
            viewport.scrollLeft =
                state.startScrollLeft -
                deltaX;
        }

        handlePointerUp(event) {
            const viewport =
                event.currentTarget;

            const state =
                this.dragState.get(
                    viewport
                );

            if (
                !state ||
                state.pointerId !==
                event.pointerId
            ) {
                return;
            }

            const placement =
                viewport.closest(
                    "[data-cart-nosto-placement]"
                );

            if (!placement) {
                this.dragState.delete(
                    viewport
                );

                return;
            }

            const slides =
                Array.from(
                    placement.querySelectorAll(
                        "[data-cart-nosto-slide]"
                    )
                );

            viewport.classList.remove(
                "is-dragging"
            );

            viewport.style
                .removeProperty(
                    "scroll-snap-type"
                );

            /*
             * Usamos el desplazamiento máximo registrado,
             * NO scrollLeft.
             */
            const deltaX =
                state.maxDeltaX;

            const SWIPE_THRESHOLD =
                40;

            let targetIndex =
                state.currentIndex;

            /*
             * Dedo hacia izquierda:
             * siguiente producto.
             */
            if (
                deltaX <=
                -SWIPE_THRESHOLD
            ) {
                targetIndex += 1;
            }

            /*
             * Dedo hacia derecha:
             * producto anterior.
             */
            else if (
                deltaX >=
                SWIPE_THRESHOLD
            ) {
                targetIndex -= 1;
            }

            /*
             * Nunca salir de los límites.
             */
            targetIndex =
                Math.max(
                    0,
                    Math.min(
                        slides.length - 1,
                        targetIndex
                    )
                );

            /*
             * Siempre terminamos exactamente
             * en un slide.
             */
            this.goToSlide(
                placement,
                targetIndex
            );

            try {
                if (
                    viewport.hasPointerCapture?.(
                        event.pointerId
                    )
                ) {
                    viewport.releasePointerCapture(
                        event.pointerId
                    );
                }
            } catch (error) {
                // No-op.
            }

            this.dragState.delete(
                viewport
            );
        }

        handleScroll(event) {
            const viewport =
                event.currentTarget;

            const placement =
                viewport.closest(
                    "[data-cart-nosto-placement]"
                );

            if (
                !placement ||
                !viewport.clientWidth
            ) {
                return;
            }

            const slides =
                Array.from(
                    placement.querySelectorAll(
                        "[data-cart-nosto-slide]"
                    )
                );

            if (!slides.length) {
                return;
            }

            let closestIndex =
                0;

            let smallestDistance =
                Infinity;

            slides.forEach(
                (
                    slide,
                    index
                ) => {
                    const distance =
                        Math.abs(
                            slide.offsetLeft -
                            viewport.scrollLeft
                        );

                    if (
                        distance <
                        smallestDistance
                    ) {
                        smallestDistance =
                            distance;

                        closestIndex =
                            index;
                    }
                }
            );

            this.updateDots(
                placement,
                closestIndex
            );
        }

        updateDots(
            placement,
            selectedIndex
        ) {
            placement
                .querySelectorAll(
                    ".cart-nosto-recommendations__dot"
                )
                .forEach(
                    (
                        dot,
                        index
                    ) => {
                        const active =
                            index ===
                            selectedIndex;

                        dot.classList.toggle(
                            "is-active",
                            active
                        );

                        dot.setAttribute(
                            "aria-current",
                            active
                                ? "true"
                                : "false"
                        );
                    }
                );
        }

        destroyCardsInsidePlacement(
            placement
        ) {
            const remainingCards =
                [];

            this.cards.forEach(
                (entry) => {
                    if (
                        entry.placement ===
                        placement
                    ) {
                        entry.card
                            ?.destroy?.();

                        return;
                    }

                    remainingCards.push(
                        entry
                    );
                }
            );

            this.cards =
                remainingCards;
        }

        destroyCards() {
            this.cards.forEach(
                (entry) => {
                    entry.card
                        ?.destroy?.();
                }
            );

            this.cards = [];
        }
    }

    if (
        !customElements.get(
            "nosto-cart-recommendations"
        )
    ) {
        customElements.define(
            "nosto-cart-recommendations",
            NostoCartRecommendations
        );
    }

    global.NostoCartRecommendations =
        NostoCartRecommendations;
})(window);